import React, { useState, useEffect, useRef } from 'react';
import './Modal.css';

const Modal = ({ isOpen, onClose, project }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const iframeRef = useRef(null);
  const modalRef = useRef(null);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Reset states when project changes
  useEffect(() => {
    if (project) {
      setIsLoading(true);
      setHasError(false);
      setRetryCount(0);
    }
  }, [project]);

  // Handle iframe load timeout
  useEffect(() => {
    if (!isOpen || !project?.hasInteractivePreview) return;

    const timer = setTimeout(() => {
      if (isLoading && iframeRef.current) {
        setHasError(true);
        setIsLoading(false);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timer);
  }, [isLoading, isOpen, project]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleIframeError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  const handleRetry = () => {
    setIsLoading(true);
    setHasError(false);
    setRetryCount(prev => prev + 1);
    
    if (iframeRef.current && project) {
      // Force reload by changing src
      const url = new URL(project.link);
      url.searchParams.set('t', Date.now().toString());
      iframeRef.current.src = url.toString();
    }
  };

  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  if (!isOpen || !project) return null;

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container" ref={modalRef}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <span className="modal-emoji">{project.emoji}</span>
            <div>
              <h2 className="modal-title">{project.title}</h2>
              <div className="modal-badges">
                <span className={`modal-category-badge category-${project.category}`}>
                  {project.category}
                </span>
                {project.hasInteractivePreview && (
                  <span className="modal-live-badge">
                    <span className="live-indicator"></span>
                    Live Preview
                  </span>
                )}
              </div>
            </div>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close modal">
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Project Description */}
          <div className="modal-description">
            <p>{project.description}</p>
            <div className="modal-tech-stack">
              {project.techStack.map((tech) => (
                <span key={tech} className="modal-tech-badge">
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Preview Section */}
          <div className="modal-preview-section">
            {project.hasInteractivePreview ? (
              <>
                {/* Loading State */}
                {isLoading && (
                  <div className="modal-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading preview...</p>
                    <span className="loading-hint">This may take a few seconds</span>
                  </div>
                )}

                {/* Error State */}
                {hasError && (
                  <div className="modal-error">
                    <span className="error-icon">⚠️</span>
                    <h3>Preview Unavailable</h3>
                    <p>Unable to load the live preview. The site may be temporarily unavailable or blocking embedded content.</p>
                    <div className="error-actions">
                      <button className="modal-btn-retry" onClick={handleRetry}>
                        ↻ Retry
                      </button>
                      <a 
                        href={project.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="modal-btn-external"
                      >
                        Open in New Tab →
                      </a>
                    </div>
                    {retryCount > 0 && (
                      <span className="retry-count">Retry attempts: {retryCount}</span>
                    )}
                  </div>
                )}

                {/* Iframe Preview with proper scaling */}
                <div className="modal-iframe-wrapper">
                  <iframe
                    ref={iframeRef}
                    src={project.link}
                    title={project.title}
                    className={`modal-iframe ${isLoading || hasError ? 'hidden' : ''}`}
                    loading="lazy"
                    sandbox="allow-scripts allow-same-origin allow-forms"
                    onLoad={handleIframeLoad}
                    onError={handleIframeError}
                    style={{
                      width: '1280px',
                      height: '1024px',
                      transformOrigin: 'top left',
                      transform: 'scale(0.65)', // Scale to fit modal
                      border: 'none'
                    }}
                  />
                </div>
              </>
            ) : (
              /* Non-interactive Preview */
              <div className="modal-static-preview">
                <div className="static-preview-content">
                  <span className="static-emoji">{project.emoji}</span>
                  <h3>Project Preview</h3>
                  <p>This project doesn't support embedded preview.</p>
                  <a 
                    href={project.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="modal-btn-external"
                  >
                    View Project →
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <a 
            href={project.link} 
            target="_blank" 
            rel="noopener noreferrer"
            className="modal-btn-primary"
          >
            <span>🔗</span>
            Open Full Project
          </a>
          <button className="modal-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default Modal;
